import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  HelpCircle,
  FileText,
  Users,
  Mail,
  Image as ImageIcon,
  Settings,
  UserCog,
  LogIn,
  AlertTriangle,
  Lightbulb
} from 'lucide-react';

const Tip = ({ children }) => (
  <div className="flex gap-2 items-start mt-3 p-3 bg-blue-50 border-l-4 border-blue-600 rounded">
    <Lightbulb className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
    <p className="text-sm text-slate-700">{children}</p>
  </div>
);

const Warning = ({ children }) => (
  <div className="flex gap-2 items-start mt-3 p-3 bg-amber-50 border-l-4 border-amber-500 rounded">
    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
    <p className="text-sm text-slate-700">{children}</p>
  </div>
);

const AdminGuide = () => {
  const sections = [
    {
      id: 'getting-started',
      icon: LogIn,
      title: 'Premiers pas',
      content: (
        <>
          <p>Le back-office se compose d'un menu latéral à gauche et d'une zone de travail à droite.</p>
          <ul className="list-disc pl-5 mt-3 space-y-1 text-sm">
            <li><strong>Tableau de bord</strong> : statistiques rapides (sections, membres, messages, images).</li>
            <li><strong>Contenus</strong> : tous les textes, sous-titres et libellés du site public.</li>
            <li><strong>Membres</strong> : annuaire de l'écosystème régional affiché sur la page d'accueil.</li>
            <li><strong>Images</strong> : bibliothèque d'images réutilisables sur le site.</li>
            <li><strong>Messages</strong> : messages reçus via le formulaire de contact.</li>
            <li><strong>Paramètres</strong> : coordonnées de l'association, adresses email destinataires.</li>
            <li><strong>Administrateurs</strong> : gestion des comptes du back-office (réservé super-admin).</li>
          </ul>
          <Tip>Vos modifications sont visibles <strong>immédiatement</strong> sur le site public après l'enregistrement — pas besoin de publier.</Tip>
        </>
      )
    },
    {
      id: 'content',
      icon: FileText,
      title: 'Modifier les contenus du site',
      content: (
        <>
          <ol className="list-decimal pl-5 space-y-2 text-sm">
            <li>Allez dans <strong>Contenus</strong>.</li>
            <li>Cliquez sur la section à modifier (Hero, Qui sommes-nous, Missions, etc.) — elle se déplie.</li>
            <li>Modifiez les champs : titre, sous-titre, contenu, libellés de boutons, etc.</li>
            <li>Cliquez sur <strong>Enregistrer</strong> en bas de la section.</li>
            <li>Le site public est mis à jour immédiatement (rafraîchissez l'onglet pour voir le résultat).</li>
          </ol>
          <Tip>Chaque section a des <strong>champs additionnels</strong> propres : libellés de boutons, intitulés, citations, listes (villes, valeurs…). N'hésitez pas à dérouler complètement la section pour tout voir.</Tip>
          <Warning>Si vous videz un champ important (par exemple le titre du Hero), la section s'affichera vide sur le site. Préférez écrire un texte court plutôt que de laisser vide.</Warning>
        </>
      )
    },
    {
      id: 'members',
      icon: Users,
      title: 'Gérer les membres de l\'écosystème',
      content: (
        <>
          <p>Les membres apparaissent dans la section <em>Un écosystème régional</em> de la page d'accueil.</p>
          <ul className="list-disc pl-5 mt-3 space-y-1 text-sm">
            <li><strong>Ajouter</strong> : bouton "Ajouter un membre" en haut à droite.</li>
            <li><strong>Modifier</strong> : icône <em>crayon</em> sur la ligne du membre.</li>
            <li><strong>Masquer / Afficher</strong> : icône <em>œil</em> — le membre reste en base mais n'apparaît plus sur le site.</li>
            <li><strong>Aperçu</strong> : icône <em>loupe</em> — affiche la vignette telle qu'elle apparaîtra.</li>
            <li><strong>Supprimer</strong> : icône <em>poubelle</em> (action irréversible).</li>
          </ul>
          <p className="mt-3 text-sm"><strong>Catégories disponibles :</strong></p>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li><em>Fondateur / Soutien</em> : affiché dans la section "Membres fondateurs et de soutien".</li>
            <li><em>Actif</em> : affiché dans la section "Membres actifs".</li>
          </ul>
          <Tip>Le champ <strong>"Ordre"</strong> contrôle l'affichage (plus petit = affiché en premier). Utilisez des incréments de 10 (10, 20, 30…) pour pouvoir insérer un nouveau membre entre deux existants sans tout renuméroter.</Tip>
          <Tip>Pour le logo, vous pouvez : (1) saisir une URL externe, ou (2) téléverser l'image dans <strong>Images</strong> puis copier l'URL générée.</Tip>
        </>
      )
    },
    {
      id: 'images',
      icon: ImageIcon,
      title: 'Téléverser et utiliser des images',
      content: (
        <>
          <ol className="list-decimal pl-5 space-y-2 text-sm">
            <li>Allez dans <strong>Images</strong>.</li>
            <li>Sélectionnez la section de destination (Hero, About, Members…) — c'est juste un tag pour s'y retrouver.</li>
            <li>Cliquez sur <strong>Téléverser</strong> et choisissez votre fichier (max 5 Mo, formats PNG/JPG/WebP).</li>
            <li>Une fois importée, cliquez sur l'icône <em>copier</em> pour récupérer l'URL.</li>
            <li>Collez cette URL dans un champ <em>logo</em> de membre ou dans un contenu HTML.</li>
          </ol>
          <Warning>Limite à <strong>5 Mo</strong> par image. Au-delà, compressez l'image (tinypng.com, squoosh.app).</Warning>
        </>
      )
    },
    {
      id: 'messages',
      icon: Mail,
      title: 'Consulter les messages de contact',
      content: (
        <>
          <p>Chaque soumission du formulaire de contact apparaît ici, du plus récent au plus ancien.</p>
          <ul className="list-disc pl-5 mt-3 space-y-1 text-sm">
            <li>Les messages <strong>non lus</strong> sont mis en évidence.</li>
            <li>Cliquez sur un message pour le lire — il est automatiquement marqué comme lu.</li>
            <li>Vous pouvez <strong>supprimer</strong> un message via l'icône poubelle.</li>
            <li>Bouton <strong>Exporter CSV</strong> : télécharger tous les messages au format tableur.</li>
          </ul>
          <Tip>Une copie de chaque message vous est aussi envoyée par email à toutes les adresses configurées dans <strong>Paramètres → Emails destinataires</strong>.</Tip>
        </>
      )
    },
    {
      id: 'settings',
      icon: Settings,
      title: 'Paramètres généraux',
      content: (
        <>
          <p>Cette page regroupe les informations transverses du site :</p>
          <ul className="list-disc pl-5 mt-3 space-y-1 text-sm">
            <li><strong>Titre du site</strong> et <strong>meta description</strong> (référencement Google).</li>
            <li><strong>Adresse, téléphone, email public</strong> affichés dans la section Contact et le pied de page.</li>
            <li><strong>Emails destinataires</strong> : la ou les adresses qui reçoivent les notifications du formulaire. Vous pouvez en ajouter plusieurs.</li>
            <li><strong>Message de confirmation</strong> : ce qui s'affiche à l'utilisateur après l'envoi du formulaire.</li>
          </ul>
          <p className="mt-3 text-sm">N'oubliez pas de cliquer sur <strong>Enregistrer</strong> en bas de page.</p>
        </>
      )
    },
    {
      id: 'admins',
      icon: UserCog,
      title: 'Gérer les administrateurs (super-admin uniquement)',
      content: (
        <>
          <p>Réservé aux comptes <strong>super-administrateur</strong>.</p>
          <ul className="list-disc pl-5 mt-3 space-y-1 text-sm">
            <li><strong>Créer</strong> un nouveau compte admin (avec ou sans privilèges super-admin).</li>
            <li><strong>Modifier</strong> nom, email, mot de passe d'un compte existant.</li>
            <li><strong>Désactiver / Activer</strong> un compte sans le supprimer (utile pour un départ temporaire).</li>
            <li><strong>Supprimer</strong> définitivement un compte.</li>
          </ul>
          <Warning>Vous ne pouvez pas supprimer ni désactiver votre propre compte. Demandez à un autre super-admin de le faire si nécessaire.</Warning>
          <Tip>Choisissez un mot de passe d'au moins 12 caractères, mélangeant majuscules, minuscules, chiffres et caractères spéciaux.</Tip>
        </>
      )
    },
    {
      id: 'troubleshooting',
      icon: AlertTriangle,
      title: 'Dépannage — questions fréquentes',
      content: (
        <>
          <p className="font-semibold text-sm">Je clique sur « Enregistrer » mais rien ne se passe.</p>
          <p className="text-sm text-slate-600 mb-3">Votre session a peut-être expiré (durée 24 h). Reconnectez-vous (lien "Se déconnecter" en bas du menu).</p>

          <p className="font-semibold text-sm">Le visiteur ne reçoit pas l'email de confirmation.</p>
          <p className="text-sm text-slate-600 mb-3">Le service d'envoi Resend exige une vérification DNS du domaine d'envoi. Tant que cette vérification n'est pas effectuée, les emails ne partent que vers l'adresse vérifiée du compte.</p>

          <p className="font-semibold text-sm">J'ai supprimé une section / un texte par erreur.</p>
          <p className="text-sm text-slate-600 mb-3">Aucun rollback automatique. Saisissez à nouveau le contenu manuellement. Pensez à copier-coller un texte important avant modifications majeures.</p>

          <p className="font-semibold text-sm">Le site public affiche encore l'ancien contenu.</p>
          <p className="text-sm text-slate-600 mb-3">Forcez le rechargement de la page (Ctrl+F5 sur Windows, ⌘+Shift+R sur Mac) pour vider le cache navigateur.</p>

          <p className="font-semibold text-sm">Je veux ajouter un membre mais la liste ne se met pas à jour.</p>
          <p className="text-sm text-slate-600">Vérifiez que la case <strong>"Visible"</strong> est cochée et que la catégorie est correctement renseignée.</p>
        </>
      )
    }
  ];

  return (
    <div className="space-y-6" data-testid="admin-guide">
      <div>
        <h2 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <HelpCircle className="h-7 w-7 text-blue-700" />
          Guide d'utilisation
        </h2>
        <p className="text-slate-600 mt-2 max-w-3xl">
          Tout ce qu'il faut savoir pour gérer le site Place Financière Bourgogne-Franche-Comté depuis ce back-office.
          Dépliez chaque section pour découvrir les étapes détaillées.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sommaire</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-blue-700 hover:underline flex items-center gap-2">
                  <s.icon className="h-4 w-4" />
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Accordion type="multiple" defaultValue={['getting-started']} className="space-y-3">
        {sections.map((s) => (
          <AccordionItem key={s.id} value={s.id} id={s.id} className="border rounded-lg bg-white shadow-sm">
            <AccordionTrigger className="px-5 py-4 hover:no-underline" data-testid={`guide-section-${s.id}`}>
              <span className="flex items-center gap-3 text-left">
                <s.icon className="h-5 w-5 text-blue-700" />
                <span className="font-semibold text-slate-900">{s.title}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 text-slate-700 leading-relaxed">
              {s.content}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-6 text-sm text-slate-700">
          <p className="font-semibold mb-2 text-blue-900">Besoin d'aide supplémentaire ?</p>
          <p>
            Pour toute question technique non couverte par ce guide, contactez votre administrateur principal
            ou l'équipe technique du site.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminGuide;
